import React from 'react';
import { PageLayout } from '../components/Layout/PageLayout';
import { Shield, Lock, Eye, Database, Bell, UserCheck, AlertCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageLayout>
      <SEO 
        title="Privacy Policy | RaveRemedy"
        description="Learn how RaveRemedy collects, uses, and protects your personal information. Our commitment to your privacy and data security."
      />
      <div className="pt-32 pb-16">
        <div className="relative mb-24">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&q=80"
              alt="Privacy Policy background"
              className="w-full h-[50vh] object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/40 to-black" />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold gradient-text mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Your privacy is important to us. This policy outlines how we collect, use, and protect your personal information.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4">
          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Shield className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Introduction</h2>
            </div>
            <p className="text-gray-300 mb-4">
              At RaveRemedy, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.
            </p>
            <p className="text-gray-300 mb-4">
              This privacy policy applies to all users of our website, regardless of how you access it. By using our website, you consent to the data practices described in this policy.
            </p>
            <p className="text-gray-300">
              Last updated: April 15, 2024
            </p>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Database className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Information We Collect</h2>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
                <p className="text-gray-300 mb-2">We may collect the following types of personal information:</p>
                <ul className="list-disc pl-6 text-gray-300 space-y-2">
                  <li>Contact information (name, email address, phone number)</li>
                  <li>Billing and shipping information</li>
                  <li>Account credentials</li>
                  <li>Purchase history and preferences</li>
                  <li>Communication history with our team</li>
                  <li>Information you provide in reviews or surveys</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3">Automatically Collected Information</h3>
                <p className="text-gray-300 mb-2">When you visit our website, our servers may automatically log the following information:</p>
                <ul className="list-disc pl-6 text-gray-300 space-y-2">
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Referral source</li>
                  <li>Length of visit and page views</li>
                  <li>Navigation paths and interaction patterns</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3">Cookies and Similar Technologies</h3>
                <p className="text-gray-300 mb-2">
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with small amounts of data which may include an anonymous unique identifier.
                </p>
                <p className="text-gray-300">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
              </div>
            </div>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Eye className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">How We Use Your Information</h2>
            </div>
            <p className="text-gray-300 mb-4">We use the collected information for various purposes, including:</p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>To provide and maintain our services</li>
              <li>To process and fulfill your orders</li>
              <li>To notify you about changes to our services</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To personalize your experience on our website</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To analyze usage patterns and improve our website</li>
              <li>To detect, prevent, and address technical issues or fraud</li>
              <li>To comply with legal obligations</li>
            </ul>
            
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-3">Legal Basis for Processing</h3>
              <p className="text-gray-300 mb-2">
                We process your personal data on the following legal bases:
              </p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Your consent</li>
                <li>Performance of a contract with you</li>
                <li>Compliance with legal obligations</li>
                <li>Our legitimate interests (such as improving our services)</li>
              </ul>
            </div>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <AlertCircle className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Information Sharing</h2>
            </div>
            <p className="text-gray-300 mb-4">
              We do not sell, trade, or rent your personal information to third parties. However, we may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Service providers who assist us in operating our website and business</li>
              <li>Payment processors to complete transactions</li>
              <li>Shipping companies to deliver products</li>
              <li>Analytics providers to help us improve our services</li>
              <li>Legal authorities when required by law</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Any third parties with whom we share your data are contractually obligated to keep your information confidential and secure.
            </p>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Lock className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Data Security</h2>
            </div>
            <p className="text-gray-300 mb-4">
              We implement appropriate security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>SSL encryption for all data transmission</li>
              <li>Regular security assessments and vulnerability testing</li>
              <li>Secure payment processing through trusted providers</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular backups and disaster recovery procedures</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p className="text-gray-300 mt-4">
              While we strive to use commercially acceptable means to protect your personal data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <UserCheck className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Your Rights</h2>
            </div>
            <p className="text-gray-300 mb-4">Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><span className="font-semibold">Right to access</span> - You have the right to request copies of your personal data.</li>
              <li><span className="font-semibold">Right to rectification</span> - You have the right to request that we correct inaccurate information.</li>
              <li><span className="font-semibold">Right to erasure</span> - You have the right to request that we delete your personal data.</li>
              <li><span className="font-semibold">Right to restrict processing</span> - You have the right to request that we restrict the processing of your data.</li>
              <li><span className="font-semibold">Right to data portability</span> - You have the right to request that we transfer your data to another organization.</li>
              <li><span className="font-semibold">Right to object</span> - You have the right to object to our processing of your personal data.</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To exercise any of these rights, please contact us using the information provided in the "Contact Us" section.
            </p>
          </div>
          
          <div className="glass-panel rounded-3xl p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Bell className="w-12 h-12 text-[#ff00ff]" />
              <h2 className="text-3xl font-bold gradient-text">Changes to This Privacy Policy</h2>
            </div>
            <p className="text-gray-300 mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
            <p className="text-gray-300">
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </div>

          <div className="glass-panel rounded-3xl p-8 md:p-12 text-center">
            <Bell className="w-16 h-16 text-[#ff00ff] mx-auto mb-6" />
            <h2 className="text-3xl font-bold gradient-text mb-4">Contact Us</h2>
            <p className="text-xl text-gray-300 mb-8">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:privacy@raveremedy.com"
                className="px-8 py-3 bg-gradient-to-r from-[#ff00ff] via-[#ff66ff] to-[#00ffff] text-white rounded-xl font-bold hover:opacity-90 transition-all"
              >
                Email Privacy Team
              </a>
              <a 
                href="/contact"
                className="px-8 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-colors"
              >
                Contact Form
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicyPage;